import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import { Link, useNavigate } from 'react-router-dom';
import { FiChevronRight, FiEdit2, FiTrash2, FiEye } from "react-icons/fi";
import { BaseInput, BaseSelect, BaseButton } from "../../components/ui";
import pagoService from '../../services/pagoService';
import useCanDelete from '../../hooks/useCanDelete';

const ESTADO_OPTIONS = [
  { value: 'TODOS', label: 'Todos' },
  { value: 'PENDIENTE', label: 'Pendiente' },
  { value: 'PAGADO', label: 'Pagado' },
  { value: 'VENCIDO', label: 'Vencido' },
];

const PagosCrud = () => {
  const navigate = useNavigate();
  const canDelete = useCanDelete();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('TODOS');
  const [filtroFecha, setFiltroFecha] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const pagosData = await pagoService.obtenerPagos();
        setPagos(pagosData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Error al cargar los pagos');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    if (name === 'estado') setFiltroEstado(value);
    if (name === 'fecha') setFiltroFecha(value);
  };

  const handleEliminarPago = async (id) => {
    if (window.confirm('¿Está seguro que desea eliminar este pago?')) {
      try {
        await pagoService.eliminarPago(id);
        setPagos(pagos.filter(pago => pago.id !== id));
        alert('Pago eliminado exitosamente');
      } catch (error) {
        console.error('Error al eliminar el pago:', error);
        alert('Error al eliminar el pago');
      }
    }
  };

  const pagosFiltrados = pagos.filter(pago => {
    const cumpleEstado = filtroEstado === 'TODOS' || pago.estado === filtroEstado;
    const cumpleFecha = !filtroFecha || pago.fechaPago.includes(filtroFecha);
    return cumpleEstado && cumpleFecha;
  });

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <Sidebar id='menu-item6' id1='menu-items6' activeClassName='contabilidad-pagos'/>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Contabilidad</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FiChevronRight icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Gestión de Pagos</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-heading">
                        <h4>Gestión de Pagos</h4>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-xl-3">
                      <BaseSelect
                        native
                        label="Filtrar por Estado"
                        name="estado"
                        value={filtroEstado}
                        onChange={handleFiltroChange}
                        options={ESTADO_OPTIONS}
                      />
                    </div>
                    <div className="col-12 col-md-6 col-xl-3">
                      <BaseInput
                        label="Filtrar por Fecha"
                        name="fecha"
                        type="month"
                        value={filtroFecha}
                        onChange={handleFiltroChange}
                      />
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Espacio</th>
                          <th>Inquilino</th>
                          <th>Monto</th>
                          <th>Fecha de Pago</th>
                          <th>Método de Pago</th>
                          <th>Estado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagosFiltrados.map((pago) => (
                          <tr key={pago.id}>
                            <td>{pago.id}</td>
                            <td>{pago.espacioNombre}</td>
                            <td>{pago.inquilinoNombre}</td>
                            <td>${pago.monto.toFixed(2)}</td>
                            <td>{pago.fechaPago}</td>
                            <td>{pago.metodoPago}</td>
                            <td>
                              <span className={`badge bg-${pago.estado === 'PAGADO' ? 'success' : pago.estado === 'PENDIENTE' ? 'warning' : 'danger'}`}>
                                {pago.estado}
                              </span>
                            </td>
                            <td>
                              <div className="actions">
                                <BaseButton
                                  type="button"
                                  variant="success"
                                  size="sm"
                                  className="me-2"
                                  icon={<FiEye />}
                                  onClick={() => navigate(`/contabilidad/pagos/ver/${pago.id}`)}
                                  title="Ver"
                                />
                                <BaseButton
                                  type="button"
                                  variant="primary"
                                  size="sm"
                                  className="me-2"
                                  icon={<FiEdit2 />}
                                  onClick={() => navigate(`/contabilidad/pagos/editar/${pago.id}`)}
                                  title="Editar"
                                />
                                {canDelete && (
                                  <BaseButton
                                    type="button"
                                    variant="danger"
                                    size="sm"
                                    icon={<FiTrash2 />}
                                    onClick={() => handleEliminarPago(pago.id)}
                                    title="Eliminar"
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PagosCrud;
